import { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import type { TarotCard } from '@yadviga/shared'

interface Props {
  card: TarotCard
  reversed: boolean
  imageUrl: string
  positionLabel?: string
  onClose: () => void
}

/**
 * Fullscreen tap-to-zoom card viewer. The card images are detailed and worth
 * looking at in full size; the small grid view in spreads doesn't do them
 * justice. Tap card → lightbox opens with the image at near-page-height. Tap
 * backdrop or press Esc → close.
 */
export function CardLightbox({ card, reversed, imageUrl, positionLabel, onClose }: Props) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language === 'en' ? 'en' : 'ru'
  const cardName = lang === 'en' ? card.nameEn : card.nameRu
  const keywords = lang === 'en' ? card.keywordsEn : card.keywordsRu
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [onClose])

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.92, y: 8 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 4 }}
          transition={{ type: 'spring', stiffness: 320, damping: 28 }}
          className="flex max-h-[92dvh] w-full max-w-[460px] flex-col items-center gap-4 px-5"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative">
            <img
              src={imageUrl}
              alt={cardName}
              className={`max-h-[72dvh] w-auto rounded-3xl shadow-[0_18px_60px_rgba(0,0,0,0.6)] ring-1 ring-cream/20 ${
                reversed ? 'rotate-180' : ''
              }`}
            />
          </div>
          <div className="text-center">
            {positionLabel && (
              <p className="mb-1 text-xs uppercase tracking-widest text-cream/55">
                {positionLabel}
              </p>
            )}
            <h3 className="font-serif text-2xl text-cream">{cardName}</h3>
            <p className="mt-1 text-sm text-cream/70">
              {keywords.slice(0, 3).join(' · ')}
            </p>
            {reversed && (
              <p className="mt-1 text-xs uppercase tracking-wider text-[#d8a376]">
                {t('tarot.reversed')}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('tarot.close')}
            className="rounded-full border border-cream/25 px-5 py-1.5 text-sm text-cream/85 transition hover:bg-cream/10"
          >
            {t('tarot.close')}
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
